import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Observable } from 'rxjs';
import { createStore, StateCreator } from 'zustand/vanilla';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ZustandAdapter } from './zustand-adapter';
import { Usuario, Perfil, PermisosVisitas } from '../core/services/autenticacion.service';
import { calcularIdJerarquiaParaEndpoint } from '../shared/utils/perfil.util';
import { createAuthPersistStorage, limpiarLegacyAuthLocalStorage } from './auth-persist.storage';
import { decodeHTMLEntities } from '../shared/utils/string.util';

/**
 * Extrae el claim `exp` (segundos desde Unix epoch) del token JWT actual sin
 * verificar la firma. Devuelve null si no se puede decodificar.
 */
function extraerExpDeToken(token: string): number | null {
  try {
    const partes = token.split('.');
    if (partes.length !== 3) {
      return null;
    }
    const payload = partes[1].replace(/-/g, '+').replace(/_/g, '/');
    const mod = payload.length % 4;
    const padded = mod === 2 ? payload + '==' : mod === 3 ? payload + '=' : mod === 1 ? payload + '===' : payload;
    const obj = JSON.parse(decodeURIComponent(escape(atob(padded))));
    return typeof obj?.exp === 'number' ? obj.exp : null;
  } catch {
    return null;
  }
}

/**
 * Interfaz del estado de autenticación
 */
export interface AuthState {
  usuario: Usuario | null;
  estaAutenticado: boolean;
  token: string | null;
  /** Refresh token para renovar la sesión sin re-login (access token de 15 min). */
  refreshToken: string | null;
  /** Proveedor de autenticación usado en el último login exitoso. */
  proveedorAuth: 'ldap' | 'claveunica' | null;
  perfilActivo: Perfil | null;
  /** ID de área seleccionada (nivel Áreas). -1 = "Todas las Áreas" o perfil Extensionista. */
  idAreaSeleccionada: number;
  /** ID de región (perfiles 4–6). -1 = sin región; perfil 6 puede usar 1 = "Todas las Regiones" (ver perfil.util). */
  idRegionSeleccionada: number;
  /** ID de jerarquía / unidad operativa (nivel UO). -1 = "Todas las Unidades Operativas" */
  unidadOperativaActiva: number;
  /** Permite usar "Ruta del día" sin conexión tras un login online previo. */
  sesionCampoHabilitada: boolean;
  /** Marca de tiempo (ms) de la última autenticación online exitosa (para expiración del modo campo). */
  ultimaAuthOnlineEn: number | null;
  /** Marca de tiempo (ms) de la última interacción del usuario (revocación por inactividad). */
  ultimaInteraccion: number | null;
  // Acciones
  iniciarSesion: (usuario: Usuario, token: string, proveedorAuth?: AuthState['proveedorAuth'], refreshToken?: string) => void;
  cerrarSesion: () => void;
  /** Actualiza access + refresh tokens (usado tras un refresh exitoso). */
  actualizarTokens: (token: string, refreshToken: string) => void;
  /** Registra la última interacción del usuario (pointerdown/keydown). */
  registrarInteraccion: () => void;
  /** Inicia el contador de inactividad (al cargar la sesión la carga cuenta como actividad). */
  iniciarContadorInactividad: () => void;
  /** true si la sesión superó `msInactividad` sin interacción del usuario. */
  inactividadSuperada: (msInactividad: number) => boolean;
  /** Nativo: quita token/sesión API sin borrar snapshot de campo (usuario, última auth online, selectores). */
  invalidarSesionApiPreservandoCampo: () => void;
  cambiarPerfilActivo: (perfil: Perfil | null) => void;
  cambiarRegionSeleccionada: (idRegion: number) => void;
  cambiarAreaSeleccionada: (idArea: number) => void;
  cambiarUnidadOperativa: (unidadId: number) => void;
  actualizarUsuario: (usuario: Partial<Usuario>) => void;
}

/**
 * Estado inicial de autenticación
 */
const initialState: Omit<
  AuthState,
  | 'iniciarSesion'
  | 'cerrarSesion'
  | 'actualizarTokens'
  | 'registrarInteraccion'
  | 'iniciarContadorInactividad'
  | 'inactividadSuperada'
  | 'invalidarSesionApiPreservandoCampo'
  | 'cambiarPerfilActivo'
  | 'cambiarRegionSeleccionada'
  | 'cambiarAreaSeleccionada'
  | 'cambiarUnidadOperativa'
  | 'actualizarUsuario'
> = {
  usuario: null,
  estaAutenticado: false,
  token: null,
  refreshToken: null,
  proveedorAuth: null,
  perfilActivo: null,
  idAreaSeleccionada: -1,
  idRegionSeleccionada: -1,
  unidadOperativaActiva: -1,
  sesionCampoHabilitada: false,
  ultimaAuthOnlineEn: null,
  ultimaInteraccion: null
};

/** Valor de id_jerarquia para "Todas las Unidades Operativas" */
export const ID_JERARQUIA_TODAS = -1;

/**
 * Creador del store de Zustand para autenticación
 */
const createAuthStore: StateCreator<AuthState> = (set, get) => ({
  ...initialState,
  
  iniciarSesion: (usuario: Usuario, token: string, proveedorAuth?: AuthState['proveedorAuth'], refreshToken?: string) => {
    const state = get();
    let perfilActivo: Perfil | null = null;
    
    if (usuario && usuario.nombre) {
      usuario = { ...usuario, nombre: decodeHTMLEntities(usuario.nombre) };
    }
    
    if (usuario.perfiles && usuario.perfiles.length > 0) {
      const idPreferido = state.perfilActivo?.id_perfil;
      const preferido = idPreferido != null
        ? usuario.perfiles.find(p => p.id_perfil === idPreferido)
        : null;
      // Usar siempre un perfil del nuevo usuario (con permisos actualizados), no el rehidratado
      perfilActivo = preferido ?? usuario.perfiles[0];
    }
    
    set({
      usuario,
      estaAutenticado: true,
      token,
      refreshToken: refreshToken ?? null,
      proveedorAuth: proveedorAuth ?? 'ldap',
      perfilActivo,
      sesionCampoHabilitada: true,
      ultimaAuthOnlineEn: Date.now(),
      ultimaInteraccion: Date.now()
    });
  },

  actualizarTokens: (token: string, refreshToken: string) => {
    const state = get();
    const exp = extraerExpDeToken(token);
    set({
      token,
      refreshToken,
      ultimaAuthOnlineEn: Date.now(),
      // Mantener `usuario.exp` sincronizado con el access token vigente para que
      // cualquier código que lo consulte no quede con un valor desactualizado.
      usuario: state.usuario && exp !== null ? { ...state.usuario, exp } : state.usuario
    });
  },


  cerrarSesion: () => {
    // Resetear perfil, área y UO para que otro usuario no herede datos del anterior
    set({
      usuario: null,
      estaAutenticado: false,
      token: null,
      refreshToken: null,
      proveedorAuth: null,
      perfilActivo: null,
      idAreaSeleccionada: -1,
      idRegionSeleccionada: -1,
      unidadOperativaActiva: -1,
      sesionCampoHabilitada: false,
      ultimaAuthOnlineEn: null,
      ultimaInteraccion: null
    });
  },

  invalidarSesionApiPreservandoCampo: () => {
    set({
      token: null,
      refreshToken: null,
      estaAutenticado: false
    });
  },

  registrarInteraccion: () => {
    set({ ultimaInteraccion: Date.now() });
  },

  iniciarContadorInactividad: () => {
    // El contador de inactividad inicia al cargar la sesión (la carga cuenta como actividad).
    set({ ultimaInteraccion: Date.now() });
  },

  inactividadSuperada: (msInactividad: number) => {
    const ultima = get().ultimaInteraccion;
    if (ultima === null) {
      return false;
    }
    return Date.now() - ultima >= msInactividad;
  },

  cambiarPerfilActivo: (perfil: Perfil | null) => {
    set({
      perfilActivo: perfil,
      idRegionSeleccionada: -1,
      idAreaSeleccionada: -1,
      unidadOperativaActiva: -1
    });
  },

  cambiarRegionSeleccionada: (idRegion: number) => {
    set({
      idRegionSeleccionada: Number(idRegion),
      idAreaSeleccionada: -1,
      unidadOperativaActiva: -1
    });
  },

  cambiarAreaSeleccionada: (idArea: number) => {
    const id = Number(idArea);
    set({
      idAreaSeleccionada: id,
      unidadOperativaActiva: -1
    });
  },

  cambiarUnidadOperativa: (unidadId: number) => {
    set({
      unidadOperativaActiva: Number(unidadId)
    });
  },

  actualizarUsuario: (usuario: Partial<Usuario>) => {
    const state = get();
    if (state.usuario) {
      set({ usuario: { ...state.usuario, ...usuario } });
    }
  }
});

/**
 * Crea el store de Zustand con el middleware `persist`.
 *
 * Se extrae a una factoría para que el tipo de retorno conserve la augmentación
 * del middleware (`store.persist`), que Zustand añade vía declaration merging sobre
 * `StoreApi`. Así obtenemos la API de hidratación tipada sin declararla a mano.
 */
function crearAuthStorePersistido() {
  return createStore<AuthState>()(
    persist(
      createAuthStore,
      {
        name: 'indap_auth_state',
        storage: createJSONStorage(() => createAuthPersistStorage()),
        version: 5,
        migrate: (persistedState: unknown, version: number) => {
          // persist pasa el estado interno (sin wrapper state/version); devolver estado migrado
          const s = (persistedState as Record<string, unknown>) ?? {};
          const migrated = { ...s } as Record<string, unknown>;
          if (migrated['unidadOperativaActiva'] === 'todas') {
            migrated['unidadOperativaActiva'] = -1;
          }
          if (migrated['idAreaSeleccionada'] === undefined) {
            migrated['idAreaSeleccionada'] = -1;
          }
          if (version < 2 || migrated['idRegionSeleccionada'] === undefined) {
            migrated['idRegionSeleccionada'] = -1;
          }
          if (version < 3) {
            migrated['sesionCampoHabilitada'] = migrated['sesionCampoHabilitada'] ?? false;
            migrated['ultimaAuthOnlineEn'] =
              migrated['ultimaAuthOnlineEn'] ??
              (migrated['estaAutenticado'] ? Date.now() : null);
          }
          if (version < 4) {
            migrated['proveedorAuth'] = migrated['proveedorAuth'] ?? null;
          }
          if (version < 5) {
            migrated['refreshToken'] = null;
          }
          delete migrated['usuarios'];
          return migrated as unknown as AuthState;
        },
      }
    )
  );
}

/** API de persistencia que Zustand expone sobre el store (tipada por el middleware). */
type AuthPersistApi = ReturnType<typeof crearAuthStorePersistido>['persist'];

/**
 * Store de autenticación usando Zustand
 */
@Injectable({
  providedIn: 'root'
})
export class AuthStore extends ZustandAdapter<AuthState> {
  /** API de persist tipada por el propio middleware: hasHydrated/onFinishHydration/rehydrate, etc. */
  private readonly persistApi: AuthPersistApi;

  constructor() {
    const store = crearAuthStorePersistido();
    limpiarLegacyAuthLocalStorage();
    super(store);
    this.persistApi = store.persist;
  }

  /** Resuelve cuando Zustand terminó de rehidrat desde persist (crítico en nativo/async). */
  esperarHidratacion(): Promise<void> {
    if (this.persistApi.hasHydrated()) {
      return Promise.resolve();
    }
    return new Promise<void>(resolve => {
      const unsub = this.persistApi.onFinishHydration(() => {
        unsub();
        resolve();
      });
    });
  }

  /** Días máximos sin login online para seguir usando "Ruta del día" offline. */
  private static readonly DIAS_MAX_MODO_CAMPO = 14;

  /**
   * Si el usuario puede entrar a /ruta-del-dia sin red (snapshot tras login online LDAP o Clave Única).
   */
  puedeUsarRutaDelDiaOffline(): boolean {
    const s = this.getState();
    const sub = String(s.usuario?.sub ?? '').trim();
    if (!s.sesionCampoHabilitada || !s.usuario || !sub || !s.ultimaAuthOnlineEn) {
      return false;
    }
    if (Capacitor.isNativePlatform()) {
      return true;
    }
    const maxMs = AuthStore.DIAS_MAX_MODO_CAMPO * 24 * 60 * 60 * 1000;
    return Date.now() - s.ultimaAuthOnlineEn < maxMs;
  }

  /**
   * Reentrada a Ruta del día: sin red y con snapshot de campo (JWT puede estar vencido).
   */
  debeEntrarRutaDelDiaSinRed(isOnline: boolean): boolean {
    if (isOnline) return false;
    return this.puedeUsarRutaDelDiaOffline();
  }

  /** Etiqueta del último proveedor de login (para CTA de modo campo). */
  etiquetaProveedorAuthCampo(): string {
    switch (this.getState().proveedorAuth) {
      case 'claveunica':
        return 'Clave Única';
      case 'ldap':
        return 'LDAP';
      default:
        return '';
    }
  }

  /**
   * Si debe evitarse cerrar sesión por token expirado (sin red, modo campo aún válido).
   */
  omitirCierrePorTokenExpiradoSinRed(isOnline: boolean): boolean {
    if (isOnline) return false;
    return this.puedeUsarRutaDelDiaOffline();
  }

  /**
   * Selectores como Observables
   */
  get usuario$(): Observable<Usuario | null> {
    return this.select(state => {
      const u = state.usuario;
      if (u && u.nombre) {
        return { ...u, nombre: decodeHTMLEntities(u.nombre) };
      }
      return u;
    });
  }


  get estaAutenticado$(): Observable<boolean> {
    return this.select(state => state.estaAutenticado);
  }

  get perfilActivo$(): Observable<Perfil | null> {
    return this.select(state => state.perfilActivo);
  }

  /**
   * Permisos de visitas del perfil activo. Se actualiza al cambiar de perfil.
   * null cuando el perfil no tiene permisos (el componente trata null como ningún permiso).
   */
  get permisosVisitas$(): Observable<PermisosVisitas | null> {
    return this.select(state => {
      const p = state.perfilActivo?.permisos?.visitas;
      if (p) return p;
      return null;
    });
  }

  get idAreaSeleccionada$(): Observable<number> {
    return this.select(state => state.idAreaSeleccionada);
  }

  get idRegionSeleccionada$(): Observable<number> {
    return this.select(state => state.idRegionSeleccionada);
  }

  get unidadOperativaActiva$(): Observable<number> {
    return this.select(state => state.unidadOperativaActiva);
  }

  /**
   * Acciones (delegadas al store de Zustand)
   */
  iniciarSesion(usuario: Usuario, token: string, refreshToken?: string): void {
    const store = this.getStore();
    store.getState().iniciarSesion(usuario, token, 'ldap', refreshToken);
  }

  iniciarSesionConProveedor(usuario: Usuario, token: string, proveedorAuth: AuthState['proveedorAuth'], refreshToken?: string): void {
    const store = this.getStore();
    store.getState().iniciarSesion(usuario, token, proveedorAuth, refreshToken);
  }

  /** Tras un refresh exitoso, actualiza access + refresh tokens (no reinicia sesión). */
  actualizarTokens(token: string, refreshToken: string): void {
    const store = this.getStore();
    store.getState().actualizarTokens(token, refreshToken);
  }

  cerrarSesion(): void {
    const store = this.getStore();
    store.getState().cerrarSesion();
  }

  /**
   * Quita JWT y bandera de sesión API conservando usuario y datos de modo campo (solo iOS/Android).
   * En web no aplica: usar `cerrarSesion()` para expiración.
   */
  invalidarSesionApiPreservandoCampo(): void {
    const store = this.getStore();
    store.getState().invalidarSesionApiPreservandoCampo();
  }

  cambiarPerfilActivo(perfil: Perfil | null): void {
    const store = this.getStore();
    store.getState().cambiarPerfilActivo(perfil);
  }

  cambiarRegionSeleccionada(idRegion: number): void {
    const store = this.getStore();
    store.getState().cambiarRegionSeleccionada(idRegion);
  }

  cambiarAreaSeleccionada(idArea: number): void {
    const store = this.getStore();
    store.getState().cambiarAreaSeleccionada(idArea);
  }

  cambiarUnidadOperativa(unidadId: number): void {
    const store = this.getStore();
    store.getState().cambiarUnidadOperativa(unidadId);
  }

  actualizarUsuario(usuario: Partial<Usuario>): void {
    const store = this.getStore();
    store.getState().actualizarUsuario(usuario);
  }

  /**
   * Getters
   */
  obtenerToken(): string | null {
    return this.getState().token;
  }

  obtenerRefreshToken(): string | null {
    return this.getState().refreshToken;
  }

  obtenerProveedorAuth(): AuthState['proveedorAuth'] {
    return this.getState().proveedorAuth;
  }

  registrarInteraccion(): void {
    const store = this.getStore();
    store.getState().registrarInteraccion();
  }

  iniciarContadorInactividad(): void {
    const store = this.getStore();
    store.getState().iniciarContadorInactividad();
  }

  /** true si la sesión superó `msInactividad` sin interacción del usuario. */
  inactividadSuperada(msInactividad: number): boolean {
    return this.getState().inactividadSuperada(msInactividad);
  }

  obtenerUltimaInteraccion(): number | null {
    return this.getState().ultimaInteraccion;
  }

  obtenerUsuarioActual(): Usuario | null {
    const u = this.getState().usuario;
    if (u && u.nombre) {
      return { ...u, nombre: decodeHTMLEntities(u.nombre) };
    }
    return u;
  }


  obtenerEstadoAutenticacion(): boolean {
    return this.getState().estaAutenticado;
  }

  obtenerPerfilActivo(): Perfil | null {
    return this.getState().perfilActivo;
  }

  /**
   * Permisos de visitas del perfil activo. null si el perfil no tiene permisos.
   */
  obtenerPermisosVisitas(): PermisosVisitas | null {
    const p = this.getState().perfilActivo?.permisos?.visitas;
    if (p) return p;
    return null;
  }

  /** ID de área seleccionada. -1 = Todas las Áreas o perfil Extensionista. */
  obtenerIdAreaSeleccionada(): number {
    return this.getState().idAreaSeleccionada;
  }

  obtenerIdRegionSeleccionada(): number {
    return this.getState().idRegionSeleccionada;
  }

  obtenerUnidadOperativaActiva(): number {
    return this.getState().unidadOperativaActiva;
  }

  /**
   * id_jerarquia para usar en endpoints (dashboard, visitas, productores, etc.).
   * Ver docs/HEADER_SELECTORES.md y calcularIdJerarquiaParaEndpoint en perfil.util.ts.
   */
  obtenerIdJerarquiaParaEndpoint(): number {
    const state = this.getState();
    return calcularIdJerarquiaParaEndpoint(
      state.perfilActivo,
      state.idRegionSeleccionada,
      state.idAreaSeleccionada,
      state.unidadOperativaActiva
    );
  }

  /**
   * Verifica si el token ha expirado
   * @returns true si el token ha expirado o no existe, false si es válido
   */
  tokenExpirado(): boolean {
    const state = this.getState();
    
    // Si no hay token o usuario, consideramos que está expirado
    if (!state.token || !state.usuario) {
      return true;
    }

    // Decodificar SIEMPRE el token JWT actual: `state.usuario.exp` puede quedar
    // desactualizado (stale) tras un refresh porque `actualizarTokens()` no lo
    // actualiza. Confiar en él haría que `tokenExpirado()` quedara en true
    // permanente después de la primera expiración, rompiendo la renovación por
    // actividad. El `exp` del token siempre es el valor vigente.
    try {
      const payload = this.decodificarTokenJWT(state.token);
      if (payload && payload.exp) {
        // exp viene en segundos desde Unix epoch, Date.now() está en milisegundos
        const ahora = Math.floor(Date.now() / 1000);
        // Consideramos expirado si faltan menos de 60 segundos (margen de seguridad)
        return payload.exp <= (ahora + 60);
      }
    } catch (error) {
      console.warn('Error al decodificar token JWT:', error);
    }

    // Si no podemos verificar la expiración, asumimos que el token es válido
    // y dejamos que el servidor lo valide
    return false;
  }

  /**
   * Segundos restantes hasta que expire el token actual (exp vigente del JWT).
   * Devuelve null si no se puede determinar (sin token o exp no decodificable).
   */
  tiempoRestanteSesionSegundos(): number | null {
    const state = this.getState();
    if (!state.token) {
      return null;
    }
    const exp = extraerExpDeToken(state.token);
    if (exp == null) {
      return null;
    }
    const ahora = Math.floor(Date.now() / 1000);
    return Math.max(0, exp - ahora);
  }

  /**
   * Indica si faltan menos de `umbralSegundos` para que expire el token.
   * Devuelve false si no se puede determinar.
   */
  faltanMenosDe(umbralSegundos: number): boolean {
    const restante = this.tiempoRestanteSesionSegundos();
    return restante != null && restante < umbralSegundos;
  }

  /**
   * Decodifica un token JWT sin verificar la firma
   * Solo para obtener el payload y verificar la expiración
   */
  private decodificarTokenJWT(token: string): any {
    try {
      const partes = token.split('.');
      if (partes.length !== 3) {
        return null;
      }
      
      // El payload es la segunda parte del token
      const payload = partes[1];
      // Decodificar de base64url a string
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.warn('Error al parsear token JWT:', error);
      return null;
    }
  }

  /**
   * Verifica si la sesión es válida (autenticado y token no expirado)
   */
  sesionValida(): boolean {
    return this.getState().estaAutenticado && !this.tokenExpirado();
  }
}

